import React, { useEffect, useState } from "react";
import { LoaderScreen } from "../../components/LoaderScreen";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { capitalizePalabras } from "../../../Helpers/CapitalizePalabras";
import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import SaveIcon from "@mui/icons-material/Save";

export const NotasSinRelacionSection = ({ data, botones }) => {
  const [notas, setNotas]                           = useState([]);
  const [anticipos, setAnticipos]                   = useState([]);
  // Nota seleccionada — identificada por serie_fiscal + v_ref
  const [selectedNota, setSelectedNota]             = useState(null);
  // Anticipo seleccionado — solo uno a la vez
  const [selectedAnticipo, setSelectedAnticipo]     = useState(null);
  const [loader, setLoader] = useState({
    mostrar: false,
    alerta:  false,
    error:   false,
    mensaje: "",
  });

  useEffect(() => {
    setAnticipos([]);
    setSelectedAnticipo(null);
    setSelectedNota(null);
    setNotas(data);
  }, [data]);

  const buildNotaKey = (serie_fiscal, v_ref, importe) =>
    `${serie_fiscal}||${v_ref}||${importe}`;

  const onSearchAnticipos = (serie_fiscal, v_ref, importe) => {
    setLoader({ ...loader, mostrar: true });
    request
      .get(
        `ListadoInvoiceMoviemientos/ObtenerListadoPosiblesAnticipos?serie_fiscal=${encodeURIComponent(serie_fiscal)}&v_ref=${encodeURIComponent(v_ref)}&importe=${encodeURIComponent(importe)}`
      )
      .then((result) => {
        setLoader({ ...loader, mostrar: false });
        setAnticipos(result ?? []);
        const preseleccionado = (result ?? []).find((x) => x.chk === 1 || x.chk === true);
        setSelectedAnticipo(preseleccionado?.folio ?? null);
      })
      .catch((error) => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("DASHBOARD FISCAL", JSON.stringify(error), "error");
      });
  };

  const handleClickNota = (nota) => {
    const importe = ((nota.cargo || 0) + (nota.abono || 0)).toFixed(2);
    const key = buildNotaKey(nota.serie_fiscal, nota.v_ref, importe);

    // Si ya está seleccionada, no vuelve a consultar
    if (selectedNota === key) return;

    setSelectedNota(key);
    setSelectedAnticipo(null);
    onSearchAnticipos(nota.serie_fiscal, nota.v_ref, importe);
  };


  const totalSeleccionado = anticipos
    .filter((ant) => selectedAnticipo === ant.folio)
    .reduce((sum, ant) => sum + (ant.cargo + ant.abono|| 0), 0);

  const importeNotaSeleccionada = (() => {
    if (!selectedNota) return 0;
    const [, , importe] = selectedNota.split("||");
    return parseFloat(importe) || 0;
  })();

  const diferencia =
    selectedAnticipo !== null
      ? Math.abs(importeNotaSeleccionada - totalSeleccionado).toFixed(2)
      : 0;

  const onAplicar = () => {
    if (!selectedAnticipo) {
      Swal.fire(
        "CORRECCION DE INCIDENCIAS",
        "Debe seleccionar un anticipo para ligar",
        "warning"
      );
      return;
    }

    if (
      diferencia !== 0 &&
      diferencia !== "0" &&
      diferencia !== "0.0" &&
      diferencia !== "0.00"
    ) {
      Swal.fire(
        "CORRECCION DE INCIDENCIAS",
        "El importe de la nota seleccionada no coincide con el del anticipo seleccionado",
        "warning"
      );
      return;
    }

    const serieFiscalAnticipo = anticipos.find(
      (ant) => ant.folio === selectedAnticipo
    )?.serie_fiscal;

    const [serieFiscalNota] = selectedNota.split("||");

    const mdl = {
      serie_fiscal_anticipo: serieFiscalAnticipo,
      serie_fiscal_nota:     serieFiscalNota,
      usuario:               0,
    };

    setLoader({ ...loader, mostrar: true });
    request
      .post("ListadoInvoiceMoviemientos/GuardarRelacionNotaAnticipo", mdl)
      .then(() => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("Éxito", "Relación guardada correctamente", "success");
      })
      .catch((error) => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("Error", JSON.stringify(error), "error");
      });
  };

  return (
    <div className="listado-fiscal-invoicemovimientos-container">
      <LoaderScreen loader={loader} />

      {/* ── Tabla 1: Notas sin relación ── */}
      <div className="listado-fiscal-invoicemovimientos-container-tabla scroll">
        <table>
          <thead>
            <tr>
              <th colSpan={9}>Notas Sin Relación</th>
            </tr>
            <tr>
              <th>Sucursal</th>
              <th><div>Cargo</div></th>
              <th><div>Abono</div></th>
              <th><div>Fecha</div></th>
              <th><div>Batch</div></th>
              <th><div>Document No.</div></th>
              <th><div>Serie Fiscal</div></th>
              <th><div>Referencia</div></th>
              <th><div>Usuario</div></th>
            </tr>
          </thead>
          <tbody>
            {notas.map((nota, i) => {
              const key      = buildNotaKey(nota.serie_fiscal, nota.v_ref, ((nota.cargo || 0) + (nota.abono || 0)).toFixed(2));
              const selected = selectedNota === key;
              return (
                <tr
                  key={i}
                  onClick={() => handleClickNota(nota)}
                  className={selected ? "selected-row" : ""}
                  style={{ cursor: "pointer" }}
                >
                  <td className="ta-center">{capitalizePalabras(nota.sucursal)}</td>
                  <td className="ta-right">{FormatMoney(nota.cargo)}</td>
                  <td className="ta-right">{FormatMoney(nota.abono)}</td>
                  <td className="ta-center">
                    {nota.fecha
                      ? new Date(nota.fecha).toLocaleDateString("es-MX", {
                          day:   "2-digit",
                          month: "2-digit",
                          year:  "numeric",
                        })
                      : "-"}
                  </td>
                  <td className="ta-center">{nota.batch}</td>
                  <td className="ta-center">{nota.document_no}</td>
                  <td className="ta-center">{nota.serie_fiscal}</td>
                  <td className="ta-left">{nota.v_ref}</td>
                  <td className="ta-center">{nota.v_usuario}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Tabla 2: Posibles anticipos ── */}
      <div className="listado-fiscal-invoicemovimientos-container-tabla scroll">
        <table>
          <thead>
            <tr>
              <th colSpan={11}>Posibles Anticipos</th>
            </tr>
            <tr>
              <th></th>
              <th>Sucursal</th>
              <th><div>Serie Fiscal</div></th>
              <th><div>Cargo</div></th>
              <th><div>Abono</div></th>
              <th><div>Fecha</div></th>
              <th><div>Estatus</div></th>
              <th><div>Batch</div></th>
              <th><div>Usuario</div></th>
            </tr>
          </thead>
          <tbody>
            {anticipos.map((ant, i) => {
              const isSelected = selectedAnticipo === ant.folio;
              return (
                <tr key={i} className={ant.bloqueado ? "block-row" : ""}>
                  <td className="ta-center">
                    <input
                      type="radio"
                      name="radio-anticipos-nota"
                      checked={isSelected}
                      disabled={ant.bloqueado || !botones?.aplicar}
                      onChange={() => setSelectedAnticipo(ant.folio)}
                    />
                  </td>
                  <td className="ta-center">{capitalizePalabras(ant.sucursal)}</td>
                  <td className="ta-center">{ant.serie_fiscal}</td>
                  <td className="ta-right">{FormatMoney(ant.cargo)}</td>
                  <td className="ta-right">{FormatMoney(ant.abono)}</td>
                  <td className="ta-center">
                    {ant.fecha
                      ? new Date(ant.fecha).toLocaleDateString("es-MX", {
                          day:   "2-digit",
                          month: "2-digit",
                          year:  "numeric",
                        })
                      : "-"}
                  </td>
                  <td className="ta-center">{ant.estatus}</td>
                  <td className="ta-center">{ant.batch}</td>
                  <td className="ta-center">{ant.usuario}</td>
                </tr>
              );
            })}

            <tr style={{ fontWeight: "bold", background: "#f3f3f3" }}>
              <td colSpan={4} className="ta-right">Diferencia</td>
              <td className="ta-right">{FormatMoney(diferencia)}</td>
              <td colSpan={5}></td>
            </tr>
          </tbody>
        </table>

        {botones?.aplicar && (
          <div className="buttons">
            <button
              className="listado-fiscal-invoicemovimientos-aplicar"
              onClick={onAplicar}
            >
              <SaveIcon /> Aplicar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
